import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import { useCallback, useMemo } from "react";
import type { KeyboardEvent } from "react";
import { useSearchStore } from "../store/useSearchStore";
import type { SearchItem } from "../GlobalSearchWithSDO.schema";
import type { ListInteractionMode } from "@certa/catalyst/hooks/useListInteraction";
import {
  SEARCH_PREVIEW_LIMIT,
  type SearchResultType,
  SEARCH_RESULT_TYPES
} from "../GlobalSearchWithSDO.constants";
import { useGlobalSearch } from "./useGlobalSearch";

export type CategorizedSearchResults = Record<
  SearchResultType,
  {
    flatData: ModelledAPIResponse.SearchResult[];
  }
>;
export const useGetPreviewData = (
  allSearchResults: CategorizedSearchResults
) => {
  return useMemo(() => {
    const results = {
      [SEARCH_RESULT_TYPES.DATA_RECORDS]:
        allSearchResults.data_records.flatData.slice(
          0,
          SEARCH_PREVIEW_LIMIT[SEARCH_RESULT_TYPES.DATA_RECORDS]
        ),
      [SEARCH_RESULT_TYPES.WORKFLOWS]:
        allSearchResults.workflows.flatData.slice(
          0,
          SEARCH_PREVIEW_LIMIT[SEARCH_RESULT_TYPES.WORKFLOWS]
        ),
      [SEARCH_RESULT_TYPES.FILES]: allSearchResults.files.flatData.slice(
        0,
        SEARCH_PREVIEW_LIMIT[SEARCH_RESULT_TYPES.FILES]
      )
    };

    const showSeeMoreButton = {
      [SEARCH_RESULT_TYPES.DATA_RECORDS]:
        allSearchResults.data_records.flatData.length >
        SEARCH_PREVIEW_LIMIT[SEARCH_RESULT_TYPES.DATA_RECORDS],
      [SEARCH_RESULT_TYPES.WORKFLOWS]:
        allSearchResults.workflows.flatData.length >
        SEARCH_PREVIEW_LIMIT[SEARCH_RESULT_TYPES.WORKFLOWS],
      [SEARCH_RESULT_TYPES.FILES]:
        allSearchResults.files.flatData.length >
        SEARCH_PREVIEW_LIMIT[SEARCH_RESULT_TYPES.FILES]
    };

    const allData = [
      ...results[SEARCH_RESULT_TYPES.DATA_RECORDS],
      ...results[SEARCH_RESULT_TYPES.WORKFLOWS],
      ...results[SEARCH_RESULT_TYPES.FILES]
    ]; // This should be in correct order as UI other wise index will be wrong when navigating via keyboard
    return {
      allData,
      ...results,
      showSeeMoreButton
    };
  }, [allSearchResults]);
};

type SearchKeyboardHandlers = {
  onSelectSearchResult: (
    result: ModelledAPIResponse.SearchResult,
    index: number,
    interactionMode: ListInteractionMode,
    query: string
  ) => void;
  onSelectRecentSearch: (
    item: SearchItem,
    index: number,
    interactionMode: ListInteractionMode
  ) => void;
  recentSearches: SearchItem[];
};

export const useSearchKeyboard = ({
  onSelectSearchResult,
  onSelectRecentSearch,
  recentSearches
}: SearchKeyboardHandlers) => {
  const {
    setSelectedItem,
    setInteractionMode,
    selectedItem,
    query,
    hasSearchQuery,
    selectedResultType
  } = useSearchStore();

  const allSearchResults = useGlobalSearch();

  const previewData = useGetPreviewData(allSearchResults);

  // Determine the currently active items based on state
  const activeItems = useMemo(() => {
    if (hasSearchQuery) {
      return selectedResultType
        ? allSearchResults[selectedResultType].flatData
        : previewData.allData;
    }
    return recentSearches;
  }, [
    hasSearchQuery,
    selectedResultType,
    allSearchResults,
    previewData.allData,
    recentSearches
  ]);

  const totalActiveItems = activeItems.length;
  const hasResults = totalActiveItems > 0;

  const handleArrowNavigation = useCallback(
    (event: KeyboardEvent<HTMLElement>, delta: number) => {
      event.preventDefault();
      setInteractionMode("keyboard");
      setSelectedItem(prev => {
        const next = prev + delta;
        return Math.max(0, Math.min(next, totalActiveItems - 1));
      });
    },
    [totalActiveItems, setInteractionMode, setSelectedItem]
  );

  const handleEnter = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const isValidSelection =
        selectedItem >= 0 && selectedItem < activeItems.length;
      if (!isValidSelection) return;

      event.preventDefault();
      const selectedActiveItem = activeItems[selectedItem];
      if (!selectedActiveItem) return;
      if (hasSearchQuery) {
        onSelectSearchResult(
          selectedActiveItem as ModelledAPIResponse.SearchResult,
          selectedItem,
          "keyboard",
          query
        );
      } else {
        onSelectRecentSearch(
          selectedActiveItem as SearchItem,
          selectedItem,
          "keyboard"
        );
      }
    },
    [
      selectedItem,
      activeItems,
      hasSearchQuery,
      onSelectSearchResult,
      query,
      onSelectRecentSearch
    ]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const keyHandlers: Record<string, () => void> = {
        ArrowDown: () => handleArrowNavigation(event, 1),
        ArrowUp: () => handleArrowNavigation(event, -1),
        Enter: () => handleEnter(event)
      };

      // Don't handle navigation if no results or loading
      if (
        (event.key === "ArrowDown" || event.key === "ArrowUp") &&
        (!hasResults || allSearchResults.isAnyQueryLoading)
      ) {
        event.preventDefault();
        return;
      }

      const handler = keyHandlers[event.key];
      if (handler) {
        handler();
      }
    },
    [
      handleArrowNavigation,
      handleEnter,
      hasResults,
      allSearchResults.isAnyQueryLoading
    ]
  );

  return { handleKeyDown };
};
